import { useState, useEffect, useRef, forwardRef } from 'react';
import debounce from 'lodash/debounce';
import SearchUtil from '../../../helpers/search';
import h from '../../../helpers/common';
import user from '../../../helpers/user';
import { __ } from '../../../translations';
import { appConfig } from '../../../config/app.config';
import { IThinkAnalyticsSearchService } from '@intigral/domain/lib/application/services/ThinkAnalyticsSearchService';
import { container } from '@intigral/domain/lib/DataDomain';
import { DI } from '@intigral/constant/config/DIContainer';
import { Search } from '@intigral/ui';
import { ISearch } from '@intigral/ui/components/Search';

type Suggestion = {
    title: string;
    formatted: string;
};

const OverlaySearchContainer = forwardRef(function OverlaySearchContainer(
    { query, open, setQuery, onOpenChange, handleSearchFormSubmit, searchError }: ISearch,
    ref
) {
    const searchUtil = new SearchUtil();
    const [suggestions, setSuggestions] = useState([]);
    const [recentSearches, setRecentSearches] = useState(searchUtil.getLastQueries(h.getMaxRecentSearchCount()));

    const toggleRecentSearches = () => {
        setSuggestions([]);
        setRecentSearches(searchUtil.getLastQueries(h.getMaxRecentSearchCount()));
    };

    const onSearchInputChange = async (searchQuery) => {
        if (searchQuery && searchUtil.shouldTriggerAutoSuggestion(searchQuery)) {
            try {
                const userInfo = user.getUserInfo();
                const userProfile = user.getProfile();
                const reqBody = {
                    productKey: appConfig.productKey,
                    query: searchQuery,
                    language: (searchUtil.getSearchLanguage(searchQuery) || 'ar').toUpperCase(),
                    userName: user.getUsername(),
                    profileName: user.getProfileName(),
                    rating: userProfile.newParentalControl || userProfile.parentalControl,
                    apiKey: user.getAPIKey(),
                };
                const taService = container.resolve<IThinkAnalyticsSearchService>(
                    DI.SERVICES.IThinkAnalyticsSearchService
                );
                const response = await taService.getSearchSuggestions(reqBody, userInfo.identityToken);
                if (response && response.data) {
                    const suggestions = response.data.entries;
                    const highlightedSuggestions: Suggestion[] = suggestions.map((suggestion) => {
                        return {
                            title: suggestion,
                            formatted: suggestion.replace(new RegExp(searchQuery, 'gi'), (match) => {
                                return `<span style="font-weight: bolder">${match}</span>`;
                            }),
                        };
                    });

                    setSuggestions(highlightedSuggestions);
                } else {
                    setSuggestions([]);
                }
            } catch {
                toggleRecentSearches();
            }
        } else if (searchQuery && searchUtil.shouldClearAutoSuggestion(searchQuery)) {
            toggleRecentSearches();
        }
    };
    const delayedAutoSuggestion = useRef(debounce((q) => onSearchInputChange(q), 500)).current;

    useEffect(() => {
        if (query) {
            delayedAutoSuggestion(query);
        } else {
            toggleRecentSearches();
        }
    }, [query]);

    const onRecentSearchClear = () => {
        searchUtil.clearLastQueries();
        setRecentSearches(searchUtil.getLastQueries(h.getMaxRecentSearchCount()));
    };

    return (
        <Search
            open={open}
            query={query}
            setQuery={setQuery}
            onOpenChange={() => onOpenChange()}
            ref={ref}
            handleSearchFormSubmit={handleSearchFormSubmit}
            clearLabel={__('search_clear_text')}
            searchError={searchError}
            suggestions={suggestions && suggestions.length > 0 ? searchUtil.getLimitedAutoSuggestions(suggestions) : []}
            queriesHistory={recentSearches}
            historyHeaderText={__('search_recent_text')}
            onRecentSearchClear={onRecentSearchClear}
        />
    );
});

export default OverlaySearchContainer;
